import type { MenuSection } from './navigationMenu';

export const NAV_OPEN_SECTIONS_STORAGE_KEY = 'teegle-nav-open-sections';

export type OpenSections = Record<string, boolean>;

const HOME_PATH = '/';

/** 以路徑區段為界：/members 涵蓋 /members/123，但不涵蓋 /membership */
function coversPath(itemPath: string, pathname: string): boolean {
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

/**
 * 目前頁面對應的選單項目。細節頁（如 /members/123）沒有自己的項目，
 * 取最長的前綴，讓 /members/consumption 不會被 /members 搶走。
 */
export function findActiveItemPath(
    sections: readonly MenuSection[],
    pathname: string
): string | undefined {
    const coveringPaths = sections
        .flatMap((section) => section.items.map((item) => item.path))
        .filter((itemPath) => coversPath(itemPath, pathname));
    return coveringPaths.sort((first, second) => second.length - first.length)[0];
}

export function isHomeActive(pathname: string): boolean {
    return pathname === HOME_PATH;
}

export function findActiveSectionKey(
    sections: readonly MenuSection[],
    pathname: string
): string | undefined {
    const activeItemPath = findActiveItemPath(sections, pathname);
    if (activeItemPath === undefined) return undefined;
    return sections.find((section) => section.items.some((item) => item.path === activeItemPath))
        ?.titleKey;
}

function isOpenSectionsRecord(value: unknown): value is OpenSections {
    return (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        Object.values(value).every((entry) => typeof entry === 'boolean')
    );
}

/** 預設只展開目前頁面所在的群組；使用者本次工作階段手動展開或收合的選擇優先 */
export function buildOpenSections(
    sections: readonly MenuSection[],
    activeSectionKey: string | undefined,
    storedSections: OpenSections
): OpenSections {
    const openSections: OpenSections = {};
    for (const section of sections) {
        openSections[section.titleKey] =
            section.titleKey === activeSectionKey || storedSections[section.titleKey] === true;
    }
    return openSections;
}

export function resolveSessionStorage(): Storage | null {
    try {
        return window.sessionStorage;
    } catch (error) {
        // 隱私模式或封鎖網站資料時存取 sessionStorage 會丟例外，選單照常運作，只是不記憶
        console.warn('sessionStorage 無法使用，選單展開狀態不會保留', error);
        return null;
    }
}

export function readStoredOpenSections(storage: Pick<Storage, 'getItem'> | null): OpenSections {
    if (storage === null) return {};
    try {
        const rawValue = storage.getItem(NAV_OPEN_SECTIONS_STORAGE_KEY);
        if (rawValue === null) return {};
        const parsedValue: unknown = JSON.parse(rawValue);
        return isOpenSectionsRecord(parsedValue) ? parsedValue : {};
    } catch (error) {
        console.warn('選單展開狀態讀取失敗，改用預設值', error);
        return {};
    }
}

export function writeStoredOpenSections(
    storage: Pick<Storage, 'setItem'> | null,
    openSections: OpenSections
): void {
    if (storage === null) return;
    try {
        storage.setItem(NAV_OPEN_SECTIONS_STORAGE_KEY, JSON.stringify(openSections));
    } catch (error) {
        console.warn('選單展開狀態寫入失敗', error);
    }
}
